import {
  isBdApiEnabled,
  apiLoadContractRequestBundle,
  apiSaveContractRequestBundle,
  setCachedContractRequestBundle,
} from './expenseApi';

/**
 * 계약의뢰 (품의 승인 후 → 계약담당 의)
 * - API(isBdApiEnabled): BdExpense · 아니면 localStorage
 * - 키: erp.expense.contractRequest|{tenant}|{fy}
 *
 * 근거: BD-DESIGN-014 · 계약실무 매뉴얼(발주준비) · 공공 계약의뢰서 관례
 */

import { getCurrentFiscalYear } from 'components/forms';
import {
  CONTRACT_OBJECT,
  CONTRACT_OBJECT_PRIMARY_OPTIONS,
  AWARD_METHOD,
  AWARD_METHOD_OPTIONS,
} from 'contract/contractStore';
import {
  EXPENSE_STATUS,
  expenseStatusLabel,
  loadProposalBundle,
  todayYmd,
  createUid,
} from 'expense/expenseStore';

export const CONTRACT_REQUEST_STORAGE_PREFIX = 'erp.expense.contractRequest';

/** 계약의뢰 전용 상태 — 품의 DRAFT/REQUESTED/APPROVED 와 구분 */
export const CONTRACT_REQUEST_STATUS = {
  DRAFT: 'DRAFT',
  REQUESTED: 'REQUESTED',
  RECEIVED: 'RECEIVED',
  REJECTED: 'REJECTED',
};

export const CONTRACT_REQUEST_STATUS_OPTIONS = [
  { value: CONTRACT_REQUEST_STATUS.DRAFT, label: '작성중' },
  { value: CONTRACT_REQUEST_STATUS.REQUESTED, label: '의뢰' },
  { value: CONTRACT_REQUEST_STATUS.RECEIVED, label: '접수' },
  { value: CONTRACT_REQUEST_STATUS.REJECTED, label: '반려' },
];

/** 계약방법(경쟁) — 매뉴얼·지방계약법 구분 */
export const CONTRACT_METHOD = {
  OPEN: 'OPEN',
  LIMITED: 'LIMITED',
  NOMINATED: 'NOMINATED',
  PRIVATE: 'PRIVATE',
};

export const CONTRACT_METHOD_OPTIONS = [
  { value: CONTRACT_METHOD.OPEN, label: '일반경쟁' },
  { value: CONTRACT_METHOD.LIMITED, label: '제한경쟁' },
  { value: CONTRACT_METHOD.NOMINATED, label: '지명경쟁' },
  { value: CONTRACT_METHOD.PRIVATE, label: '수의계약' },
];

/** 첨부 문서유형 — 의뢰 시 사업부서 제출 관례 */
export const CONTRACT_REQUEST_DOC_TYPE = {
  QUOTE: 'QUOTE',
  BIZ_PLAN: 'BIZ_PLAN',
  SOW: 'SOW',
  SPEC: 'SPEC',
  COST: 'COST',
  POLICY: 'POLICY',
  OTHER: 'OTHER',
};

export const CONTRACT_REQUEST_DOC_TYPE_OPTIONS = [
  { value: CONTRACT_REQUEST_DOC_TYPE.QUOTE, label: '견적서' },
  { value: CONTRACT_REQUEST_DOC_TYPE.BIZ_PLAN, label: '사업계획서' },
  { value: CONTRACT_REQUEST_DOC_TYPE.SOW, label: '과업지시서' },
  { value: CONTRACT_REQUEST_DOC_TYPE.SPEC, label: '규격서' },
  { value: CONTRACT_REQUEST_DOC_TYPE.COST, label: '산출내역서' },
  { value: CONTRACT_REQUEST_DOC_TYPE.POLICY, label: '방침서' },
  { value: CONTRACT_REQUEST_DOC_TYPE.OTHER, label: '기타' },
];

export { CONTRACT_OBJECT, CONTRACT_OBJECT_PRIMARY_OPTIONS, AWARD_METHOD, AWARD_METHOD_OPTIONS };

function labelOf(options, code) {
  return options.find((o) => o.value === code)?.label || code || '';
}

export function contractRequestStatusLabel(code) {
  return labelOf(CONTRACT_REQUEST_STATUS_OPTIONS, code);
}

export function contractMethodLabel(code) {
  return labelOf(CONTRACT_METHOD_OPTIONS, code);
}

export function contractRequestDocTypeLabel(code) {
  return labelOf(CONTRACT_REQUEST_DOC_TYPE_OPTIONS, code);
}

function storageKey(dims = {}) {
  return [
    CONTRACT_REQUEST_STORAGE_PREFIX,
    String(dims.tenantCode || '').trim(),
    String(dims.fiscalYear || '').trim(),
  ].join('|');
}

function readBundle(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return { masters: [], docs: [] };
    const data = JSON.parse(raw);
    return {
      masters: Array.isArray(data?.masters) ? data.masters : [],
      docs: Array.isArray(data?.docs) ? data.docs : [],
    };
  } catch {
    return { masters: [], docs: [] };
  }
}

function writeBundle(key, payload) {
  localStorage.setItem(
    key,
    JSON.stringify({
      ...payload,
      savedAt: new Date().toISOString(),
    })
  );
}

export function createContractRequestUid() {
  return createUid('CRQ');
}

export function createContractRequestDocUid() {
  return createUid('CRD');
}

export function emptyContractRequestMaster(partial = {}) {
  return {
    requestUid: '',
    requestNo: '',
    fiscalYear: '',
    requestDate: '',
    status: CONTRACT_REQUEST_STATUS.DRAFT,
    /** 승인 품의 연결 */
    proposalUid: '',
    proposalNo: '',
    /** 건명·개요 */
    title: '',
    overview: '',
    objectType: CONTRACT_OBJECT.GOODS,
    estimateAmt: 0,
    /** 예산축 (품의 승계) */
    deptCode: '',
    deptName: '',
    projectCode: '',
    projectName: '',
    subjectCode: '',
    subjectName: '',
    fundCode: '',
    fundName: '',
    balanceUid: '',
    allocAmt: 0,
    /** 기간·장소 */
    periodFrom: '',
    periodTo: '',
    deliveryPlace: '',
    /** 계약·낙찰 방법 */
    contractMethod: CONTRACT_METHOD.OPEN,
    awardMethod: AWARD_METHOD.OTHER,
    jointContractYn: 'N',
    bidQualReq: '',
    /** 수의 시 */
    privateContractBasis: '',
    privateContractReason: '',
    suggestVendorName: '',
    /** 하자·사회적가치·사전규격 */
    defectWarrantyPeriod: '',
    defectBondRate: '',
    socialValueYn: 'N',
    hopeEnterpriseYn: 'N',
    preSpecOpenYn: 'N',
    /** 의뢰자 */
    requestDeptCode: '',
    requestDeptName: '',
    requesterName: '',
    remark: '',
    registerDate: '',
    requestAt: '',
    receiveDate: '',
    rejectReason: '',
    /** 접수 후 계약대장 연결 (Phase B) */
    contractUid: '',
    contractNo: '',
    mod: '',
    ...partial,
  };
}

export function emptyContractRequestDocRow(partial = {}) {
  return {
    docUid: '',
    requestUid: '',
    docType: CONTRACT_REQUEST_DOC_TYPE.QUOTE,
    /** 파일 메타 — 바이너리는 IndexedDB(contractRequestFileStore) */
    fileName: '',
    mimeType: '',
    fileSize: 0,
    fileExt: '',
    viewableYn: 'N',
    hasFile: 'N',
    remark: '',
    mod: '',
    ...partial,
  };
}

export async function loadContractRequestBundle(dims) {
  if (isBdApiEnabled()) return apiLoadContractRequestBundle(dims);
  const raw = readBundle(storageKey(dims));
  // 과거 저장분에 남은 A/U는 이미 커밋된 상태 — 상태열(+) 잔상 제거
  const bundle = {
    masters: (raw.masters || []).map(clearCommittedMod),
    docs: (raw.docs || []).map(clearCommittedMod),
  };
  setCachedContractRequestBundle(dims, bundle);
  return bundle;
}

/** 저장 완료 행의 A/U 표시 제거 (삭제 D만 유지) */
export function clearCommittedMod(row) {
  if (!row || row.mod === 'D') return row;
  if (row.mod === 'A' || row.mod === 'U') return { ...row, mod: '' };
  return row;
}

export async function saveContractRequestBundle(dims, masters, docs) {
  const payload = {
    masters: (masters || []).map(clearCommittedMod),
    docs: (docs || []).map(clearCommittedMod),
  };
  if (isBdApiEnabled()) {
    return apiSaveContractRequestBundle(
      dims,
      payload.masters,
      payload.docs,
      createContractRequestUid,
      createContractRequestDocUid
    );
  }
  writeBundle(storageKey(dims), payload);
  return loadContractRequestBundle(dims);
}

export function nextContractRequestNo(masters = []) {
  const nums = (masters || [])
    .filter((m) => m.mod !== 'D')
    .map((m) => Number(String(m.requestNo || '').replace(/\D/g, '')))
    .filter((n) => Number.isFinite(n) && n > 0);
  const max = nums.length ? Math.max(...nums) : 0;
  return String(max + 1).padStart(4, '0');
}

function matchMulti(filterCsv, value) {
  const keys = String(filterCsv || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!keys.length) return true;
  const v = String(value || '').trim();
  return keys.some((k) => v === k || v.includes(k));
}

export function filterContractRequests(masters, filter = {}) {
  let list = (masters || []).filter((m) => m.mod !== 'D');
  const st = String(filter.status || '').trim();
  if (st) list = list.filter((m) => m.status === st);
  if (filter.deptCode || filter.deptName) {
    list = list.filter(
      (m) => matchMulti(filter.deptCode, m.deptCode) || matchMulti(filter.deptName, m.deptName)
    );
  }
  if (filter.objectType) {
    list = list.filter((m) => m.objectType === filter.objectType);
  }
  const kw = String(filter.keyword || '').trim();
  if (kw) {
    list = list.filter((m) => {
      const blob = [m.requestNo, m.title, m.proposalNo, m.overview, m.remark].join(' ');
      return blob.includes(kw);
    });
  }
  const from = String(filter.dateFrom || '').trim();
  const to = String(filter.dateTo || '').trim();
  if (from || to) {
    list = list.filter((m) => {
      const d = String(m.requestDate || '').trim();
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }
  return list;
}

export function filterContractRequestsForRegister(masters, filter = {}) {
  return filterContractRequests(masters, filter);
}

/** 접수 화면 — 기본 의뢰 상태 */
export function filterContractRequestsForReceive(masters, filter = {}) {
  return filterContractRequests(masters, {
    ...filter,
    status: filter.status || CONTRACT_REQUEST_STATUS.REQUESTED,
  });
}

export function enrichContractRequestRow(row) {
  return {
    ...row,
    statusNm: contractRequestStatusLabel(row.status),
    objectTypeNm: labelOf(CONTRACT_OBJECT_PRIMARY_OPTIONS, row.objectType),
    contractMethodNm: contractMethodLabel(row.contractMethod),
  };
}

/** 승인 품의 목록 — 계약의뢰 불러오기용 */
export async function listApprovedProposalsForContractRequest(dims) {
  const fy = String(dims.fiscalYear || getCurrentFiscalYear()).trim();
  const bundle = await loadProposalBundle({ tenantCode: dims.tenantCode, fiscalYear: fy });
  return (bundle.masters || [])
    .filter((m) => m.mod !== 'D' && m.status === EXPENSE_STATUS.APPROVED)
    .map((m) => ({
      ...m,
      id: m.proposalUid,
      statusNm: expenseStatusLabel(m.status),
    }));
}

/** 품의 → 의뢰 마스터 필드 승계 */
export function applyProposalToContractRequest(setFields, proposal) {
  if (!proposal || typeof setFields !== 'function') return;
  setFields({
    proposalUid: proposal.proposalUid || '',
    proposalNo: proposal.proposalNo || '',
    title: proposal.remark || proposal.title || '',
    estimateAmt: Number(proposal.proposalAmt) || 0,
    deptCode: proposal.deptCode || '',
    deptName: proposal.deptName || '',
    projectCode: proposal.projectCode || '',
    projectName: proposal.projectName || '',
    subjectCode: proposal.subjectCode || '',
    subjectName: proposal.subjectName || '',
    fundCode: proposal.fundCode || '',
    fundName: proposal.fundName || '',
    balanceUid: proposal.balanceUid || '',
    allocAmt: Number(proposal.allocAmt) || 0,
    requestDeptCode: proposal.deptCode || '',
    requestDeptName: proposal.deptName || '',
    objectType:
      proposal.proposalType === 'SERVICE'
        ? CONTRACT_OBJECT.SERVICE
        : proposal.proposalType === 'GOODS'
          ? CONTRACT_OBJECT.GOODS
          : CONTRACT_OBJECT.GOODS,
  });
}

export function submitContractRequest(masters, docs, requestUid) {
  const uid = String(requestUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.requestUid !== uid) return m;
    return {
      ...m,
      status: CONTRACT_REQUEST_STATUS.REQUESTED,
      requestAt: new Date().toISOString(),
      requestDate: m.requestDate || todayYmd(),
    };
  });
  return { masters: nextM, docs: docs || [] };
}

export function receiveContractRequest(masters, docs, requestUid) {
  const uid = String(requestUid || '').trim();
  const today = todayYmd();
  const nextM = (masters || []).map((m) => {
    if (m.requestUid !== uid) return m;
    return {
      ...m,
      status: CONTRACT_REQUEST_STATUS.RECEIVED,
      receiveDate: today,
    };
  });
  return { masters: nextM, docs: docs || [] };
}

export function rejectContractRequest(masters, docs, requestUid, reason = '반려') {
  const uid = String(requestUid || '').trim();
  const nextM = (masters || []).map((m) => {
    if (m.requestUid !== uid) return m;
    return {
      ...m,
      status: CONTRACT_REQUEST_STATUS.REJECTED,
      rejectReason: reason,
    };
  });
  return { masters: nextM, docs: docs || [] };
}

export { todayYmd };
